import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useT } from "../lib/i18n";

/**
 * Premium fullscreen lightbox — "art gallery" experience.
 *
 *   ┌────────────────────────────────────────────────────────────┐
 *   │ 03 / 27                                              [×]   │
 *   │                                                            │
 *   │                                                            │
 *   │              [ ◀ ]    PHOTO (cinéma)    [ ▶ ]              │
 *   │                                                            │
 *   │                                                            │
 *   │ TITLE (italic display)                          ·  PAYSAGE │
 *   │ « reflective caption from images.js »                      │
 *   └────────────────────────────────────────────────────────────┘
 *
 *  • Keyboard: ESC closes, ← / → navigates.
 *  • Backdrop click closes.
 *  • Smooth crossfade between photos.
 */
export const PortfolioLightbox = ({ items, index, onClose, onPrev, onNext }) => {
  const t = useT();
  const open = index !== null && index !== undefined;
  const item = open ? items[index] : null;

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, onPrev, onNext]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="portfolio-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[80] bg-ink/95 backdrop-blur-md"
          onClick={onClose}
        >
          {/* Subtle warm grain accents */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none opacity-50"
            style={{
              background:
                "radial-gradient(ellipse at 50% 0%, rgba(184,169,154,0.08) 0%, rgba(0,0,0,0) 55%), radial-gradient(ellipse at 50% 100%, rgba(184,169,154,0.04) 0%, rgba(0,0,0,0) 60%)",
            }}
          />

          {/* Top bar: counter + close */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 md:px-10 py-5 md:py-7 text-bone z-10">
            <div className="flex items-center gap-3">
              <span className="font-display text-lg tracking-[0.32em] text-bone/70">
                {String(index + 1).padStart(2, "0")}
              </span>
              <span className="h-px w-8 bg-stone/60" />
              <span className="text-[10px] tracking-[0.32em] uppercase text-bone/40">
                {String(items.length).padStart(2, "0")}
              </span>
            </div>
            <button
              data-testid="lightbox-close"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label="Fermer"
              className="group flex items-center gap-3 text-bone/70 hover:text-bone transition-colors"
            >
              <span className="text-[10px] tracking-[0.28em] uppercase hidden md:inline">
                Fermer
              </span>
              <span className="w-10 h-10 rounded-full ring-1 ring-bone/20 flex items-center justify-center group-hover:ring-bone/60 transition-all">
                <X size={18} />
              </span>
            </button>
          </div>

          {/* Prev / Next */}
          <button
            data-testid="lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            aria-label="Image précédente"
            className="group absolute left-4 md:left-10 top-1/2 -translate-y-1/2 z-10 w-12 h-12 md:w-14 md:h-14 rounded-full ring-1 ring-bone/20 hover:ring-bone/60 flex items-center justify-center text-bone/70 hover:text-bone hover:-translate-x-1 transition-all"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            aria-label="Image suivante"
            className="group absolute right-4 md:right-10 top-1/2 -translate-y-1/2 z-10 w-12 h-12 md:w-14 md:h-14 rounded-full ring-1 ring-bone/20 hover:ring-bone/60 flex items-center justify-center text-bone/70 hover:text-bone hover:translate-x-1 transition-all"
          >
            <ChevronRight size={22} />
          </button>

          {/* Stage: photo + caption */}
          <div
            className="absolute inset-0 flex flex-col items-center justify-center px-6 md:px-24 pt-20 md:pt-24 pb-32 md:pb-28"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.img
              key={item.id}
              src={item.src}
              alt={item.title}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-[70vh] md:max-h-[72vh] max-w-full w-auto object-contain shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)]"
              data-testid="lightbox-image"
            />

            {/* Caption block — title left, category right, reflective quote underneath */}
            <motion.div
              key={`cap-${item.id}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 md:mt-10 w-full max-w-3xl text-bone"
            >
              <div className="flex items-start justify-between gap-6 mb-3">
                <h2
                  data-testid="lightbox-title"
                  className="font-display text-2xl md:text-4xl italic leading-tight"
                >
                  {item.title}
                </h2>
                <span className="text-[10px] tracking-[0.28em] uppercase text-stone whitespace-nowrap pt-2">
                  {t(`cat.${item.cat}`)}
                </span>
              </div>
              {item.caption && (
                <p
                  data-testid="lightbox-caption"
                  className="font-display italic text-base md:text-lg text-bone/65 max-w-2xl leading-relaxed"
                >
                  « {item.caption} »
                </p>
              )}
              <div className="h-px w-12 bg-stone/60 mt-4" />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
